/**
 * Task Evidence Modal
 * Story 289: View completion evidence for tasks
 * Story 405: Code diff display, collapsible sections, scrollable modal
 *
 * Displays task body, completion evidence, code diffs, test results,
 * and feedback for completed tasks. All sections are collapsible accordions.
 */

import { useState, useEffect } from 'react';
import { html as diff2html } from 'diff2html';
import 'diff2html/bundles/css/diff2html.min.css';
import { MarkdownPreview } from '../editor/MarkdownPreview';
import './TaskEvidenceModal.css';

// ============================================================================
// Types
// ============================================================================

/** File change entry from evidence */
interface FileChange {
  path: string;
  change: string;  // API returns string, display handles any value
  lines?: string;
}

/** Per-file diff data from parsed_diff */
interface FileDiff {
  path: string;
  additions: number;
  deletions: number;
  content: string;
  truncated?: boolean;
}

/** Parsed diff structure from evidence */
interface ParsedDiff {
  files: FileDiff[];
  totalAdditions: number;
  totalDeletions: number;
}

/** Evidence structure from task completion */
interface Evidence {
  files_changed?: FileChange[];
  rationale?: string;
  parsed_diff?: ParsedDiff;
}

/** Test results structure */
interface TestResults {
  passed?: number;
  failed?: number;
  coverage?: number;
}

/** Feedback structure from task completion */
interface Feedback {
  scope_accurate?: boolean;
  test_summary?: string;
  lessons_learned?: string;
  blocked_reasons?: string[];
  unexpected_discoveries?: string;
}

/** Task data with evidence fields */
export interface TaskWithEvidence {
  seq: number;
  title: string;
  body?: string | null;
  evidence?: Evidence | null;
  test_results?: TestResults | null;
  feedback?: Feedback | null;
}

interface TaskEvidenceModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Called when modal should close */
  onClose: () => void;
  /** Story external ID for display */
  storyExternalId: string;
  /** Task data with evidence */
  task: TaskWithEvidence;
}

/** Badge styles for file change types */
const CHANGE_STYLES: Record<string, { bg: string; color: string }> = {
  added: { bg: 'var(--status-success-bg, #dcfce7)', color: 'var(--status-success, #16a34a)' },
  modified: { bg: 'var(--status-warning-bg, #fef3c7)', color: 'var(--status-warning, #d97706)' },
  deleted: { bg: 'var(--status-error-bg, #fee2e2)', color: 'var(--status-error, #dc2626)' },
};

const DEFAULT_CHANGE_STYLE = { bg: 'var(--bg-muted, #f1f5f9)', color: 'var(--text-secondary, #64748b)' };

// ============================================================================
// CollapsibleSection
// ============================================================================

interface CollapsibleSectionProps {
  title: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({ title, defaultExpanded = true, children }: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <section className="task-evidence-modal__section">
      <button
        type="button"
        className="task-evidence-modal__section-header"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <span
          className={`task-evidence-modal__chevron ${isExpanded ? 'task-evidence-modal__chevron--expanded' : ''}`}
          aria-hidden="true"
        >
          &#9654;
        </span>
        <h3 className="task-evidence-modal__section-title">{title}</h3>
      </button>
      {isExpanded && (
        <div className="task-evidence-modal__section-content">
          {children}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// DiffSection
// ============================================================================

interface DiffSectionProps {
  parsedDiff: ParsedDiff;
}

/**
 * Build a minimal unified diff string that diff2html can parse.
 * Prepends the git diff header lines to the stored content.
 */
function buildUnifiedDiff(file: FileDiff): string {
  const header = [
    `diff --git a/${file.path} b/${file.path}`,
    `--- a/${file.path}`,
    `+++ b/${file.path}`,
  ].join('\n');
  return `${header}\n${file.content}`;
}

function DiffSection({ parsedDiff }: DiffSectionProps) {
  return (
    <div className="task-evidence-modal__diff">
      <div className="task-evidence-modal__diff-summary">
        +{parsedDiff.totalAdditions} / &minus;{parsedDiff.totalDeletions}
      </div>
      {parsedDiff.files.map((file, idx) => (
        <DiffFileBlock key={idx} file={file} />
      ))}
    </div>
  );
}

function DiffFileBlock({ file }: { file: FileDiff }) {
  let diffHtml: string;
  try {
    const unifiedDiff = buildUnifiedDiff(file);
    diffHtml = diff2html(unifiedDiff, {
      outputFormat: 'line-by-line',
      drawFileList: false,
    });
  } catch {
    // Fallback: render raw content in monospace
    diffHtml = `<pre class="task-evidence-modal__diff-fallback">${escapeHtml(file.content)}</pre>`;
  }

  return (
    <div className="task-evidence-modal__diff-file">
      <div className="task-evidence-modal__diff-file-header">
        <span className="task-evidence-modal__diff-file-path">{file.path}</span>
        <span className="task-evidence-modal__diff-file-stats">
          <span className="task-evidence-modal__diff-additions">+{file.additions}</span>
          {' '}
          <span className="task-evidence-modal__diff-deletions">&minus;{file.deletions}</span>
        </span>
      </div>
      <div
        className="task-evidence-modal__diff-content"
        dangerouslySetInnerHTML={{ __html: diffHtml }}
      />
      {file.truncated && (
        <div className="task-evidence-modal__diff-truncated">
          Content truncated for storage
        </div>
      )}
    </div>
  );
}

/** Escape HTML special characters for fallback rendering */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ============================================================================
// TaskEvidenceModal
// ============================================================================

export function TaskEvidenceModal({
  isOpen,
  onClose,
  storyExternalId,
  task,
}: TaskEvidenceModalProps) {
  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const taskId = `${storyExternalId}.${task.seq}`;
  const hasBody = task.body && task.body.trim().length > 0;
  const hasFilesChanged = task.evidence?.files_changed && task.evidence.files_changed.length > 0;
  const hasDiff = task.evidence?.parsed_diff && task.evidence.parsed_diff.files.length > 0;
  const hasRationale = task.evidence?.rationale && task.evidence.rationale.trim().length > 0;
  const hasTestResults = task.test_results && (
    task.test_results.passed !== undefined || task.test_results.failed !== undefined
  );
  const hasFeedback = task.feedback && (
    task.feedback.scope_accurate !== undefined ||
    task.feedback.test_summary ||
    task.feedback.lessons_learned ||
    (task.feedback.blocked_reasons && task.feedback.blocked_reasons.length > 0) ||
    task.feedback.unexpected_discoveries
  );

  const hasAnyContent = hasBody || hasFilesChanged || hasDiff || hasRationale || hasTestResults || hasFeedback;

  return (
    <div className="task-evidence-modal__backdrop" onClick={onClose}>
      <div
        className="task-evidence-modal__content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="task-evidence-modal__header">
          <div>
            <h2 className="task-evidence-modal__title">Evidence of Completion</h2>
            <p className="task-evidence-modal__subtitle">
              Task {taskId}: {task.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="task-evidence-modal__close"
            title="Close"
            aria-label="Close task evidence modal"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="task-evidence-modal__body">
          {/* 1. Task Body */}
          {hasBody && (
            <CollapsibleSection title="Task Description">
              <MarkdownPreview content={task.body!} />
            </CollapsibleSection>
          )}

          {/* 2. Files Changed */}
          {hasFilesChanged && (
            <CollapsibleSection title="Files Changed">
              <table className="task-evidence-modal__files-table">
                <thead>
                  <tr>
                    <th>Path</th>
                    <th>Change</th>
                    <th>Lines</th>
                  </tr>
                </thead>
                <tbody>
                  {task.evidence!.files_changed!.map((file, idx) => {
                    const style = CHANGE_STYLES[file.change] || DEFAULT_CHANGE_STYLE;
                    return (
                      <tr key={idx}>
                        <td className="task-evidence-modal__file-path">{file.path}</td>
                        <td>
                          <span
                            className="task-evidence-modal__change-badge"
                            style={{ backgroundColor: style.bg, color: style.color }}
                          >
                            {file.change}
                          </span>
                        </td>
                        <td className="task-evidence-modal__file-lines">
                          {file.lines || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CollapsibleSection>
          )}

          {/* 3. Code Diff (NEW - Story 405) */}
          {hasDiff && (
            <CollapsibleSection title="Code Diff">
              <DiffSection parsedDiff={task.evidence!.parsed_diff!} />
            </CollapsibleSection>
          )}

          {/* 4. Rationale */}
          {hasRationale && (
            <CollapsibleSection title="Rationale">
              <MarkdownPreview content={task.evidence!.rationale!} />
            </CollapsibleSection>
          )}

          {/* 5. Test Results */}
          {hasTestResults && (
            <CollapsibleSection title="Test Results">
              <div className="task-evidence-modal__test-results">
                <div className="task-evidence-modal__test-stat task-evidence-modal__test-stat--passed">
                  <span className="task-evidence-modal__test-label">Passed</span>
                  <span className="task-evidence-modal__test-value">
                    {task.test_results!.passed ?? 0}
                  </span>
                </div>
                <div className="task-evidence-modal__test-stat task-evidence-modal__test-stat--failed">
                  <span className="task-evidence-modal__test-label">Failed</span>
                  <span className="task-evidence-modal__test-value">
                    {task.test_results!.failed ?? 0}
                  </span>
                </div>
                {task.test_results!.coverage !== undefined && (
                  <div className="task-evidence-modal__test-stat task-evidence-modal__test-stat--coverage">
                    <span className="task-evidence-modal__test-label">Coverage</span>
                    <span className="task-evidence-modal__test-value">
                      {task.test_results!.coverage}%
                    </span>
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* 6. Feedback */}
          {hasFeedback && (
            <CollapsibleSection title="Feedback">
              <div className="task-evidence-modal__feedback">
                {task.feedback!.scope_accurate !== undefined && (
                  <div className="task-evidence-modal__feedback-item">
                    <span className="task-evidence-modal__feedback-label">Scope Accurate</span>
                    <span className={`task-evidence-modal__feedback-value task-evidence-modal__feedback-value--${task.feedback!.scope_accurate ? 'yes' : 'no'}`}>
                      {task.feedback!.scope_accurate ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
                {task.feedback!.test_summary && (
                  <div className="task-evidence-modal__feedback-item task-evidence-modal__feedback-item--full">
                    <span className="task-evidence-modal__feedback-label">Test Summary</span>
                    <p className="task-evidence-modal__feedback-text">{task.feedback!.test_summary}</p>
                  </div>
                )}
                {task.feedback!.lessons_learned && (
                  <div className="task-evidence-modal__feedback-item task-evidence-modal__feedback-item--full">
                    <span className="task-evidence-modal__feedback-label">Lessons Learned</span>
                    <MarkdownPreview content={task.feedback!.lessons_learned} />
                  </div>
                )}
                {task.feedback!.blocked_reasons && task.feedback!.blocked_reasons.length > 0 && (
                  <div className="task-evidence-modal__feedback-item task-evidence-modal__feedback-item--full">
                    <span className="task-evidence-modal__feedback-label">Blocked Reasons</span>
                    <ul className="task-evidence-modal__blocked-list">
                      {task.feedback!.blocked_reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {task.feedback!.unexpected_discoveries && (
                  <div className="task-evidence-modal__feedback-item task-evidence-modal__feedback-item--full">
                    <span className="task-evidence-modal__feedback-label">Unexpected Discoveries</span>
                    <MarkdownPreview content={task.feedback!.unexpected_discoveries} />
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* No content state */}
          {!hasAnyContent && (
            <div className="task-evidence-modal__empty">
              <p>No evidence data available for this task.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="task-evidence-modal__footer">
          <button onClick={onClose} className="task-evidence-modal__close-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
